import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    NavigationStart,
    ResolveStart,
    ActivatedRouteSnapshot
} from '@angular/router';
import { Ng1NgxUrlHandlingStrategyService } from '../../routing/services/ng1-ngx-url-handling-strategy.service';
import { filter, map } from 'rxjs/operators';
import { FeatureToggleService } from '@farmnet/fnx-components';
import { AngularJsLocationSyncService } from './angular-js-location-sync.service';
import RouteData from './route-data.interface';
import { FNW_NAVIGATION_ROUTE, FnwLocationUpdate, FnwReceiveMessage, Section } from '@farmnet/webapp-loader-companion';
import { Data } from '@angular/router/src/config';
import { fromEvent } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class AngularLocationSyncService {
    data: RouteData | null;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private urlHandlingStrategy: Ng1NgxUrlHandlingStrategyService,
        private featureToggleService: FeatureToggleService,
        private angularJsLocationSyncService: AngularJsLocationSyncService,
        @Inject('navigationService') private navigationService: any,
        private ngZone: NgZone
    ) {
    }

    public startLocationSync() {
        this.onNavigationStart();
        this.onResolveStart();
        this.onNavigationEnd();

        fromEvent(window, 'message').pipe(
            filter((event: MessageEvent) => event.origin === location.origin),
            map((event: MessageEvent) => event.data),
            filter(
                (data: FnwReceiveMessage) => data.type && data.type.startsWith('fnw-')
            )
        ).subscribe((data) => {
            switch (data.type) {
                case 'fnw-test-navigate-in-service': {
                    if(this.urlHandlingStrategy.isAngularRoute()) {
                        this.ngZone.run(() => {
                            this.router.navigateByUrl(data.url)
                        })
                    }
                    break;
                }
                case 'fnw-test-navigate-to-service': {
                    if(this.urlHandlingStrategy.isAngularRoute()) {
                        this.ngZone.run(() => {
                            this.router.navigateByUrl('/' + FNW_NAVIGATION_ROUTE, { skipLocationChange: true} )
                        })
                    }
                    break;
                }
            }

        });
    }

    private onNavigationStart() {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationStart),
            )
            .subscribe((event: NavigationStart) => {
                // Navigation should be managed by Angular
                this.angularJsLocationSyncService.managedByAngularLocationSync = true;
            });
    }

    /**
     * Resolve the incoming router module data.
     * {@link https://github.com/angular/angular/issues/19420#issuecomment-417050238 Angular Issue}
     */
    private onResolveStart() {
        this.router.events
            .pipe(
                filter(event => event instanceof ResolveStart),
                map((event: ResolveStart) => {
                    let data: Data | null = null;
                    let route: ActivatedRouteSnapshot | null = event.state.root;

                    while (route) {
                        data = route.data || data;
                        route = route.firstChild;
                    }

                    return data;
                }),
            )
            .subscribe((data: RouteData) => {
                this.data = data;
            });
    }

    private onNavigationEnd() {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
            )
            .subscribe((event: NavigationEnd) => {
                if (this.navigationService.isNavigationCancelled()) {
                    // Navigation circle for Angular is finished we ensure to reset cancel state on navigationService
                    this.navigationService.resetNavigationCancelled();
                    return;
                }
                if(!this.urlHandlingStrategy.shouldProcessUrl(event.url)) { return; }

                // Navigation managed by Angular
                const section = this.getSectionFromActivatedRoute();
                const message: FnwLocationUpdate = {
                    type: 'fnw-location-update',
                    url: event.url,
                    serviceName: '',
                    section,
                };

                parent.postMessage(message, location.origin);

                // Release Navigation Management
                this.angularJsLocationSyncService.managedByAngularLocationSync = false;
            });
    }

    private getSectionFromActivatedRoute(): Section {
        let section: Section = 'dashboard';

        if(this.data !== null && this.data.section === undefined) {
            console.warn('deprecation warning: navigationService should not be used to set section anymore, add section to route definition');
        }

        if(this.data !== null && this.data.section !== undefined) {
            section = this.data.section as Section;
        }

        return section;
    }
}
